/**
 * FP3-053 — Super Admin context selector.
 *
 * Persistent dropdown rendered at the top of the sidebar (super_admin
 * only). Mirrors the "All Schools / Org / School" picker pattern
 * from Frame Interventions so a platform owner always knows which
 * org/school the operational admin pages are scoped to.
 *
 * Behavior
 *   • Default state: "All Organizations" — admin pages stay
 *     platform-wide (no `?as_org` set).
 *   • Selecting an org → sets `?as_org={id}` on the current admin
 *     route. Org name is shown collapsed: "Frame Practice Demo Org".
 *   • Selecting a school inside an org → sets
 *     `?as_org={id}&as_school={schoolId}`. Header shows
 *     "Frame Practice Demo Org · Lincoln Elementary".
 *   • Selection persists across navigation (URL) and across
 *     reloads (localStorage — see useSuperAdminContext).
 *
 * Visual
 *   Compact card with a building icon, the active label, and a
 *   carret. Clicking opens a stacked list of orgs; each org row
 *   expands to show its schools. Solo-provider orgs typically
 *   carry exactly one "Primary Workspace" school.
 */
import { useEffect, useRef, useState } from "react";
import { Buildings, CaretDown, GraduationCap, Check, House } from "@phosphor-icons/react";
import { useSuperAdminContext } from "@/lib/useSuperAdminContext";

export default function SuperAdminContextSelector() {
  const {
    isSuperAdmin,
    orgId,
    schoolId,
    orgName,
    schoolName,
    tree,
    loading,
    setContext,
    clearContext,
  } = useSuperAdminContext();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());
  const wrapRef = useRef(null);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Auto-expand the currently-selected org so the school list is
  // visible the moment the dropdown opens.
  useEffect(() => {
    if (orgId) {
      setExpanded((prev) => {
        const next = new Set(prev);
        next.add(orgId);
        return next;
      });
    }
  }, [orgId, open]);

  if (!isSuperAdmin) return null;

  const activeLabel = (() => {
    if (!orgId) return "All Organizations";
    if (orgName && schoolName) return `${orgName} · ${schoolName}`;
    if (orgName) return orgName;
    return "Loading…";
  })();

  const toggleExpand = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div
      ref={wrapRef}
      className="relative px-3 pt-3 pb-2"
      data-testid="super-admin-context-selector"
    >
      <button
        type="button"
        data-testid="super-admin-context-trigger"
        onClick={() => setOpen((v) => !v)}
        className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg border text-left transition-colors ${
          open
            ? "bg-[#E6F1F8] border-[#1F4F73]/30"
            : "bg-white border-[#E5E3DB] hover:bg-[#F3F1EC]"
        }`}
        title="Switch organization or school context"
      >
        <Buildings
          size={16}
          weight="duotone"
          className="text-[#1F4F73] shrink-0"
        />
        <span
          data-testid="super-admin-context-active-label"
          className="text-[12.5px] font-medium text-[#1F2937] truncate flex-1 min-w-0"
        >
          {activeLabel}
        </span>
        <CaretDown
          size={12}
          weight="bold"
          className={`text-[#6B7280] shrink-0 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open && (
        <div
          data-testid="super-admin-context-menu"
          className="absolute left-3 right-3 mt-1 z-50 rounded-lg border border-[#E5E3DB] bg-white shadow-lg max-h-[60vh] overflow-y-auto"
        >
          {/* "All Organizations" — clears context entirely. */}
          <button
            type="button"
            data-testid="super-admin-context-all-orgs"
            onClick={() => {
              clearContext();
              setOpen(false);
            }}
            className={`w-full flex items-center gap-2 px-3 py-2 text-[12.5px] text-left transition-colors border-b border-[#F1F2F4] ${
              !orgId
                ? "bg-[#E6F1F8] text-[#0F3554] font-semibold"
                : "text-[#3F4756] hover:bg-[#F3F1EC]"
            }`}
          >
            <House size={14} weight="duotone" className="text-[#1F4F73] shrink-0" />
            <span className="flex-1">All Organizations</span>
            {!orgId && <Check size={12} weight="bold" className="text-[#1F4F73]" />}
          </button>

          {loading ? (
            <div className="px-3 py-3 text-[11.5px] text-[#6B7280]">
              Loading organizations…
            </div>
          ) : tree.length === 0 ? (
            <div className="px-3 py-3 text-[11.5px] text-[#6B7280]">
              No organizations available.
            </div>
          ) : (
            tree.map((o) => {
              const isCurrentOrg = orgId === o.id;
              const isExpanded = expanded.has(o.id);
              const hasSchools = (o.schools || []).length > 0;
              return (
                <div
                  key={o.id}
                  data-testid={`super-admin-context-org-${o.id}`}
                  className="border-b border-[#F1F2F4] last:border-0"
                >
                  <div className="flex">
                    <button
                      type="button"
                      data-testid={`super-admin-context-org-select-${o.id}`}
                      onClick={() => {
                        setContext({ orgId: o.id, schoolId: "" });
                        setOpen(false);
                      }}
                      className={`flex-1 flex items-center gap-2 px-3 py-2 text-[12.5px] text-left transition-colors min-w-0 ${
                        isCurrentOrg && !schoolId
                          ? "bg-[#E6F1F8] text-[#0F3554] font-semibold"
                          : "text-[#1F2937] hover:bg-[#F3F1EC]"
                      }`}
                    >
                      <Buildings
                        size={14}
                        weight="duotone"
                        className="text-[#1F4F73] shrink-0"
                      />
                      <span className="truncate flex-1">{o.name}</span>
                      {/* demo / test tags surface to remind the
                          operator that they're viewing classified
                          data (only render when show_hidden_data is
                          on — otherwise these orgs aren't returned). */}
                      {(o.is_demo || o.is_curated_demo) && (
                        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#F5EFE3] text-[#7A6A2C] font-semibold">
                          demo
                        </span>
                      )}
                      {o.is_test && (
                        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#F8EFEA] text-[#7A3A2C] font-semibold">
                          test
                        </span>
                      )}
                      {isCurrentOrg && !schoolId && (
                        <Check size={12} weight="bold" className="text-[#1F4F73]" />
                      )}
                    </button>
                    {hasSchools && (
                      <button
                        type="button"
                        data-testid={`super-admin-context-org-expand-${o.id}`}
                        onClick={() => toggleExpand(o.id)}
                        className="px-2.5 text-[#6B7280] hover:text-[#1F2937] hover:bg-[#F3F1EC] transition-colors border-l border-[#F1F2F4]"
                        title={isExpanded ? "Collapse schools" : "Expand schools"}
                      >
                        <CaretDown
                          size={12}
                          weight="bold"
                          className={`transition-transform ${
                            isExpanded ? "rotate-180" : "-rotate-90"
                          }`}
                        />
                      </button>
                    )}
                  </div>
                  {hasSchools && isExpanded && (
                    <div className="bg-[#FAFAF7] border-t border-[#F1F2F4]">
                      {o.schools.map((s) => {
                        const isCurrentSchool =
                          isCurrentOrg && schoolId === s.id;
                        return (
                          <button
                            key={s.id}
                            type="button"
                            data-testid={`super-admin-context-school-${s.id}`}
                            onClick={() => {
                              setContext({ orgId: o.id, schoolId: s.id });
                              setOpen(false);
                            }}
                            className={`w-full flex items-center gap-2 pl-9 pr-3 py-1.5 text-[12px] text-left transition-colors ${
                              isCurrentSchool
                                ? "bg-[#E6F1F8] text-[#0F3554] font-semibold"
                                : "text-[#4B5563] hover:bg-[#F3F1EC]"
                            }`}
                          >
                            <GraduationCap
                              size={12}
                              weight="duotone"
                              className="text-[#3F4756] shrink-0"
                            />
                            <span className="truncate flex-1">{s.name}</span>
                            {isCurrentSchool && (
                              <Check
                                size={12}
                                weight="bold"
                                className="text-[#1F4F73]"
                              />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )}

          {/* FP3-053 — Persistent "Back to Platform Dashboard" link
              at the foot of the dropdown. With the giant blue banner
              removed, this is the dedicated escape hatch back to
              the platform control center. Renders for super_admin
              only (the whole component is gated). */}
          <a
            href="/platform/dashboard"
            data-testid="super-admin-context-back-to-platform"
            className="block w-full px-3 py-2 text-[11.5px] text-[#1F4F73] hover:bg-[#E6F1F8] hover:text-[#0F3554] transition-colors border-t border-[#F1F2F4]"
            onClick={() => setOpen(false)}
          >
            ← Back to Platform Dashboard
          </a>
        </div>
      )}
    </div>
  );
}
